/**
 * Optimizer Types
 * TypeScript interfaces for the MIP Optimizer module.
 */

/**
 * Configuration for running the optimizer.
 */
export interface OptimizerConfig {
    capacity_limit: number;      // CC - max movements/hour (1-100)
    min_turnaround: number;      // Minimum turnaround in minutes (15-180)
    lambda_weight: number;       // Weight for slot creation (0-1)
    max_slots: number;           // Maximum slots to create (1-100)
    day_start: number;           // Operational start (minutes from midnight)
    day_end: number;             // Operational end (minutes from midnight)
    time_limit_seconds: number;  // Solver time limit
    capacity_source?: 'as_is' | 'to_be';  // size slots vs as-is or to-be CC envelope
    cc_override?: number;        // explicit CC override (mov/h)
}

/**
 * Default optimizer configuration.
 */
export const DEFAULT_OPTIMIZER_CONFIG: OptimizerConfig = {
    capacity_limit: 30.0,
    min_turnaround: 45,
    lambda_weight: 0.5,
    max_slots: 20,
    day_start: 360,      // 06:00
    day_end: 1380,       // 23:00
    time_limit_seconds: 30.0
};

/**
 * Single flight input for optimization.
 */
export interface FlightInput {
    flight_id: string;
    stand: string;
    SIBT: number;        // Scheduled In-Block Time (minutes from midnight)
    SOBT: number;        // Scheduled Off-Block Time (minutes from midnight)
    aircraft_type?: string;
}

/**
 * A created slot opportunity from optimization.
 */
export interface SlotOpportunity {
    slot_id: string;
    start_time: number;    // minutes from midnight
    end_time: number;      // minutes from midnight
    duration: number;      // minutes
    stand: string;
    revenue_potential: number;
    turnaround_segment: 'narrow' | 'wide';
}

/**
 * Result from the MIP optimizer.
 */
export interface MIPOptimizerResult {
    slots_created: SlotOpportunity[];
    total_slack_recovered: number;        // minutes
    fragmentation_reduction: number;      // percentage
    solve_time_ms: number;
    status: OptimizerStatus;
    objective_value: number;
    gap_percentage: number;
    original_total_slack: number;
    original_fragmentation: number;
    // Canonical Part-5 KPIs and Tri-Node compliance. Present when the optimizer
    // ran over a real project (the async job); absent on the synthetic demo path.
    kpis?: OptimizerKpis;
    node_compliance?: NodeComplianceHour[];
    provenance?: Record<string, any>;
    // Set by the async job when it declined to run (e.g. no stand allocation to
    // optimise) instead of solving invented flights.
    error?: string;
}

/** Canonical Part-5 KPIs (optimizer_mip). equity_index = 1 - Gini. */
export interface OptimizerKpis {
    fragmentation_reduction: number;   // 0..1
    utilization_gain: number;          // ratio, mov_opt / mov_base
    equity_index: number;              // 0..1 (1 - Gini over slots by carrier)
    violation_count: number;
    node_compliance_ok: boolean;
}

/** Per-hour Tri-Node envelope check: slots may only use the node's headroom. */
export interface NodeComplianceHour {
    hour: number;
    cap_cc: number;        // controlling capacity for the hour
    existing_mov: number;  // movements already scheduled that hour
    headroom: number;      // cap_cc - existing_mov
    slots_used: number;    // slots the optimizer placed in that hour
}

/**
 * Possible optimizer status values.
 */
export type OptimizerStatus =
    | 'OPTIMAL'
    | 'FEASIBLE'
    | 'INFEASIBLE'
    | 'TIMEOUT'
    | 'ERROR'
    | 'NOT_RUN'
    | 'NO_CANDIDATES'
    | 'NO_VIABLE_SLOTS'
    | 'SOLVER_ERROR'
    | 'NO_ALLOCATION';   // async job declined: no per-flight stand allocation

/**
 * Response from the test endpoint.
 */
export interface OptimizerTestResponse {
    message: string;
    config_used: OptimizerConfig;
    result: MIPOptimizerResult;
    synthetic_flights_count: number;
}

/**
 * Optimizer status response.
 */
export interface OptimizerStatusResponse {
    status: 'available' | 'unavailable';
    ortools_installed: boolean;
    solver: string | null;
    message: string;
}

/**
 * Request body for running optimizer with flights.
 */
export interface OptimizerRunRequest {
    config: OptimizerConfig;
    flights?: FlightInput[];
}

/**
 * Candidate slot (before optimization).
 */
export interface CandidateSlot {
    slot_id: string;
    stand: string;
    start_time: number;
    end_time: number;
    duration: number;
    preceding_flight: string | null;
    following_flight: string | null;
}

/**
 * Response from candidates endpoint.
 */
export interface CandidatesResponse {
    project_id: number;
    total_candidates: number;
    candidates: CandidateSlot[];
}

/**
 * Helper to convert minutes to HH:MM format.
 */
export function minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Helper to convert HH:MM to minutes from midnight.
 */
export function timeToMinutes(time: string): number {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
}

/**
 * Flight representation for visualization.
 */
export interface ScheduleBlock {
    id: string;
    stand: string;
    startTime: number;
    endTime: number;
    type: 'flight' | 'gap' | 'slot';
    label?: string;
    color?: string;
}


// =============================================
// Slot Catalog Types (Issue #12)
// =============================================

/**
 * Enriched slot entry in the catalog.
 */
export interface SlotCatalogEntry {
    slot_id: string;
    stand: string;
    start_time: number;
    end_time: number;
    duration: number;
    segment: 'narrow' | 'wide';
    revenue_estimate: number;
    compatible_aircraft: string[];
    utilization_score: number;
    turnaround_segment: string;
}

/**
 * Response from the slot catalog endpoint.
 */
export interface SlotCatalogResponse {
    entries: SlotCatalogEntry[];
    total_slots: number;
    total_revenue_potential: number;
    narrow_slots: number;
    wide_slots: number;
    avg_duration: number;
    avg_utilization: number;
    generation_time_ms: number;
    optimizer_status: string;
}

/**
 * Request for generating a slot catalog.
 */
export interface SlotCatalogRequest {
    flights: FlightInput[];
    config: OptimizerConfig;
    base_revenue_rate?: number;
}


// =============================================
// Flight Assignment Types (Issue #13)
// =============================================

/**
 * A pending flight waiting for slot assignment.
 */
export interface PendingFlight {
    flight_id: string;
    aircraft_type: string;
    requested_time: number;
    min_duration?: number;
    priority?: number;
}

/**
 * A single flight-to-slot assignment result.
 */
export interface FlightAssignmentResult {
    flight_id: string;
    slot_id: string;
    stand: string;
    start_time: number;
    end_time: number;
    aircraft_type: string;
    segment: string;
    revenue_estimate: number;
    fit_score: number;
}

/**
 * An unassigned flight.
 */
export interface UnassignedFlight {
    flight_id: string;
    aircraft_type: string;
    reason: string;
}

/**
 * Request for automatic flight assignment.
 */
export interface AutoAssignRequest {
    flights: FlightInput[];
    pending_flights: PendingFlight[];
    config: OptimizerConfig;
    base_revenue_rate?: number;
    respect_segment?: boolean;
}

/**
 * Response from the auto-assign endpoint.
 */
export interface AutoAssignResponse {
    assignments: FlightAssignmentResult[];
    unassigned: UnassignedFlight[];
    total_assigned: number;
    total_unassigned: number;
    total_revenue: number;
    assignment_time_ms: number;
}

